/**
 * H4.2B — Sales Reconciliation (staff-facing name; internal sale_type="pos_difference").
 *
 * Reuse-only workflow: reads the already-recorded Cash/Bank/Credit totals for a
 * chosen Branch + Sales Date from the EXISTING `GET /sales` per-method totals
 * (repeat-safe because that aggregate includes any prior pos_difference rows),
 * lets staff enter the ACTUAL amounts, auto-computes the Difference, and posts
 * ONLY positive differences through the EXISTING `POST /sales` engine
 * (VAT / journals / receivable / reports / H3 realtime all reused).
 *
 * Safeguards:
 *  - Positive-only  -> post allowed (records just the deltas).
 *  - Any negative   -> BLOCK all posting (never nets, never auto-creates a
 *                      negative sale / refund / JE). Shows "Review Required".
 *  - Matched (all 0) -> "No Adjustment Required", post disabled.
 * No ZATCA: POST /sales never touches the ZATCA path.
 */
import { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cur } from '@/lib/currency';
import { localToday, localDateTimeStamp } from '@/lib/dateRange';
import { CheckCircle2, AlertTriangle, Scale } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const METHODS = [
  { key: 'cash', label: 'Cash' },
  { key: 'bank', label: 'Bank' },
  { key: 'credit', label: 'Credit' },
];
const EPS = 0.005;
const fmt = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function SalesReconciliationDialog({ open, onOpenChange, branches = [], onPosted, defaultBranchId = '', canRecord = true }) {
  const [branchId, setBranchId] = useState('');
  const [date, setDate] = useState(localToday());
  const [recorded, setRecorded] = useState({ cash: 0, bank: 0, credit: 0 });
  const [actual, setActual] = useState({ cash: '', bank: '', credit: '' });
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [posting, setPosting] = useState(false);

  // Reset when opened
  useEffect(() => {
    if (open) {
      // Preselect a branch when the caller provides one (e.g. the POS passes
      // its active branch). Web callers omit it → unchanged "Select branch…".
      setBranchId(defaultBranchId || '');
      setDate(localToday());
      setRecorded({ cash: 0, bank: 0, credit: 0 });
      setActual({ cash: '', bank: '', credit: '' });
      setLoaded(false);
    }
  }, [open, defaultBranchId]);

  const loadRecorded = useCallback(async (bid, d) => {
    if (!bid || !d) { setLoaded(false); return; }
    setLoading(true);
    setLoaded(false);
    try {
      const { data } = await api.get(`/sales?branch_id=${encodeURIComponent(bid)}&start_date=${d}&end_date=${d}&limit=1`);
      const rec = {
        cash: Number(data.cash_total || 0),
        bank: Number(data.bank_total || 0),
        credit: Number(data.credit_total || 0),
      };
      setRecorded(rec);
      // Pre-fill Actual with Recorded so an untouched method = matched (0 diff).
      setActual({ cash: String(rec.cash), bank: String(rec.bank), credit: String(rec.credit) });
      setLoaded(true);
    } catch {
      toast.error('Failed to load recorded totals');
      setLoaded(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Recalculate from the selected Branch/Date immediately (no stale totals).
  useEffect(() => {
    if (open && branchId && date) loadRecorded(branchId, date);
    else setLoaded(false);
  }, [open, branchId, date, loadRecorded]);

  const diffOf = (k) => (parseFloat(actual[k]) || 0) - (recorded[k] || 0);
  const diffs = { cash: diffOf('cash'), bank: diffOf('bank'), credit: diffOf('credit') };
  const anyNegative = METHODS.some((m) => diffs[m.key] < -EPS);
  const anyPositive = METHODS.some((m) => diffs[m.key] > EPS);
  const allMatched = !anyNegative && !anyPositive;

  const recordedTotal = recorded.cash + recorded.bank + recorded.credit;
  const actualTotal = METHODS.reduce((s, m) => s + (parseFloat(actual[m.key]) || 0), 0);
  const diffTotal = actualTotal - recordedTotal;

  // Post state: positive-only -> allowed; any negative -> blocked; matched -> nothing.
  const canPost = canRecord && loaded && anyPositive && !anyNegative && !posting;

  const methodLabel = (k) => {
    const v = diffs[k];
    if (v > EPS) return { text: `+${fmt(v)} Missing`, cls: 'text-emerald-600' };
    if (v < -EPS) return { text: `${fmt(v)} Review Required`, cls: 'text-red-600' };
    return { text: 'Matched', cls: 'text-muted-foreground' };
  };

  const handlePost = async () => {
    if (!canPost) return;
    const posDetails = METHODS
      .filter((m) => diffs[m.key] > EPS)
      .map((m) => ({ mode: m.key, amount: Math.round(diffs[m.key] * 100) / 100 }));
    if (!posDetails.length) return;
    const amount = posDetails.reduce((s, p) => s + p.amount, 0);
    const branchName = branches.find((b) => b.id === branchId)?.name || branchId;
    setPosting(true);
    try {
      await api.post('/sales', {
        sale_type: 'pos_difference',
        branch_id: branchId,
        customer_id: '',
        platform_id: '',
        payment_mode: posDetails[0].mode,
        payment_details: posDetails,
        discount: 0,
        amount,
        date: localDateTimeStamp(date),
        notes: `Sales Reconciliation adjustment — ${branchName} — ${date}`,
      });
      toast.success('Reconciliation adjustment recorded');
      onOpenChange(false);
      onPosted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record adjustment');
    } finally {
      setPosting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[calc(100vw-1.5rem)] max-h-[90dvh] overflow-y-auto overflow-x-hidden p-4 sm:p-6 [&>*]:min-w-0" data-testid="sales-reconciliation-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <Scale size={18} className="text-primary" /> Sales Reconciliation
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 min-w-0">
          <p className="text-xs text-muted-foreground">
            Pick a branch and sales date. We load the amounts already recorded, you enter the actual amounts, and only the
            positive difference is recorded — no manual maths, no duplicate invoices.
          </p>

          <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0">
            <div>
              <Label className="text-xs">Branch</Label>
              <select
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                className="mt-1 h-9 w-full rounded-lg border border-border bg-background text-sm px-2"
                data-testid="reconcile-branch-select"
              >
                <option value="">Select branch…</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Sales Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1 h-9"
                data-testid="reconcile-date-input"
              />
            </div>
          </div>

          {!branchId ? (
            <div className="text-center text-sm text-muted-foreground py-6" data-testid="reconcile-hint">
              Select a branch to load recorded totals.
            </div>
          ) : loading ? (
            <div className="text-center text-sm text-muted-foreground py-6" data-testid="reconcile-loading">Loading recorded totals…</div>
          ) : loaded ? (
            <>
              <div className="overflow-x-auto max-w-full rounded-xl border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left px-1 py-2 sm:p-2 text-xs font-medium">Method</th>
                      <th className="text-right px-1 py-2 sm:p-2 text-xs font-medium">Recorded</th>
                      <th className="text-right px-1 py-2 sm:p-2 text-xs font-medium">Actual</th>
                      <th className="text-right px-1 py-2 sm:p-2 text-xs font-medium">Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {METHODS.map((m) => {
                      const lbl = methodLabel(m.key);
                      return (
                        <tr key={m.key} className="border-t border-border">
                          <td className="px-1 py-2 sm:p-2 text-xs font-medium">{m.label}</td>
                          <td className="px-1 py-2 sm:p-2 text-right text-xs" data-testid={`reconcile-recorded-${m.key}`}>{fmt(recorded[m.key])}</td>
                          <td className="px-1 py-2 sm:p-2 text-right">
                            <Input
                              type="number"
                              step="0.01"
                              value={actual[m.key]}
                              onChange={(e) => setActual({ ...actual, [m.key]: e.target.value })}
                              className="h-8 w-[4.5rem] px-1 sm:w-28 ml-auto text-right text-xs"
                              data-testid={`reconcile-actual-${m.key}`}
                            />
                          </td>
                          <td className={`p-2 text-right text-xs font-medium ${lbl.cls}`} data-testid={`reconcile-diff-${m.key}`}>
                            {lbl.text}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 border-border bg-muted/30 font-semibold">
                      <td className="px-1 py-2 sm:p-2 text-xs">Total</td>
                      <td className="px-1 py-2 sm:p-2 text-right text-xs" data-testid="reconcile-recorded-total">{fmt(recordedTotal)}</td>
                      <td className="px-1 py-2 sm:p-2 text-right text-xs" data-testid="reconcile-actual-total">{fmt(actualTotal)}</td>
                      <td className="px-1 py-2 sm:p-2 text-right text-xs" data-testid="reconcile-diff-total">{fmt(diffTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {allMatched ? (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="reconcile-status" data-state="matched">
                  <CheckCircle2 size={16} /> Matched — No Adjustment Required
                </div>
              ) : anyNegative ? (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="reconcile-status" data-state="blocked">
                  <AlertTriangle size={16} className="mt-0.5" />
                  <span>Negative difference — Review Required. Nothing will be posted until the negative discrepancy is reviewed. Amounts are not netted.</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="reconcile-status" data-state="postable">
                  <CheckCircle2 size={16} /> {canRecord ? 'Ready to record' : 'Positive difference'} {fmt(METHODS.reduce((s, m) => s + Math.max(0, diffs[m.key]), 0))}{canRecord ? ' of positive difference.' : ' — ask a manager to record.'}
                </div>
              )}
            </>
          ) : null}

          {!canRecord && loaded && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="reconcile-view-only">
              View only — a manager or admin must record any difference.
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" className="rounded-xl" onClick={() => onOpenChange(false)} data-testid="reconcile-cancel-button">{canRecord ? 'Cancel' : 'Close'}</Button>
            {canRecord && (
              <Button size="sm" className="rounded-xl" disabled={!canPost} onClick={handlePost} data-testid="reconcile-post-button">
                {posting ? 'Recording…' : 'Record Difference'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
